<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <div class="header-actions">
        <select v-model="selectedStoreId" class="form-control" @change="handleStoreChange">
          <option value="">請選擇門市</option>
          <option v-for="store in stores" :key="store.id" :value="store.id">
            {{ store.storeName }}
          </option>
        </select>
        <input
          v-model="selectedDate"
          type="date"
          class="form-control"
          :min="minDate"
          @change="handleDateChange"
        />
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchTableStatus">重試</button>
    </div>

    <div v-else-if="tableStatus.length > 0" class="status-grid">
      <div
        v-for="status in tableStatus"
        :key="status.tableId"
        class="table-status-card"
        :class="getStatusClass(status)"
      >
        <div class="table-header">
          <h3>{{ status.tableNumber }}</h3>
          <span class="table-type">{{ status.tableType || '-' }}</span>
        </div>
        <div class="table-info">
          <div class="info-item">
            <span class="label">容納人數：</span>
            <span class="value">{{ status.capacity }}人</span>
          </div>
          <div class="info-item">
            <span class="label">狀態：</span>
            <span :class="getStatusBadgeClass(status.status)">
              {{ getStatusText(status.status) }}
            </span>
          </div>
          <div v-if="status.currentReservation" class="info-item">
            <span class="label">當前訂位：</span>
            <span class="value">{{ status.currentReservation.contactName || '-' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <p>{{ selectedStoreId ? '沒有座位資料' : '請先選擇門市' }}</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { reservationService } from '@/api/modules/reservations.js';
import { storeService } from '@/api/modules/stores.js';

export default {
  name: 'TableStatus',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('座位使用狀態');
    const stores = ref([]);
    const tableStatus = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const selectedStoreId = ref('');
    const selectedDate = ref(new Date().toISOString().split('T')[0]);

    const minDate = computed(() => {
      return new Date().toISOString().split('T')[0];
    });

    const fetchStores = async () => {
      try {
        const response = await storeService.getStores({ page: 1, pageSize: 100 });
        stores.value = response.success && response.data?.items ? response.data.items : [];
        if (!selectedStoreId.value && stores.value.length === 1) {
          selectedStoreId.value = stores.value[0].id;
          await fetchTableStatus();
        }
      } catch (err) {
        console.error('Failed to fetch stores:', err);
      }
    };

    const fetchTableStatus = async () => {
      if (!selectedStoreId.value || !selectedDate.value) {
        tableStatus.value = [];
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        const response = await reservationService.getTableStatus(
          selectedStoreId.value,
          selectedDate.value
        );
        
        if (response.success) {
          tableStatus.value = Array.isArray(response.data) ? response.data : (response.data?.items || []);
        } else {
          throw new Error('查詢失敗');
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '查詢座位狀態失敗';
        console.error('Failed to fetch table status:', err);
      } finally {
        loading.value = false;
      }
    };

    const handleStoreChange = () => {
      if (selectedStoreId.value && selectedDate.value) {
        fetchTableStatus();
      }
    };

    const handleDateChange = () => {
      if (selectedStoreId.value && selectedDate.value) {
        fetchTableStatus();
      }
    };

    const handleBack = () => {
      router.push('/client/tables');
    };

    const getStatusText = (status) => {
      const statusMap = {
        'available': '可用',
        'reserved': '已預訂',
        'occupied': '使用中',
        'maintenance': '維護中'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        'available': 'badge badge-success',
        'reserved': 'badge badge-warning',
        'occupied': 'badge badge-danger',
        'maintenance': 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getStatusClass = (status) => {
      return `status-${status.status}`;
    };

    onMounted(() => {
      fetchStores();
    });

    return {
      pageTitle,
      stores,
      tableStatus,
      loading,
      error,
      selectedStoreId,
      selectedDate,
      minDate,
      handleStoreChange,
      handleDateChange,
      handleBack,
      fetchTableStatus,
      getStatusText,
      getStatusBadgeClass,
      getStatusClass
    };
  }
};
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.table-status-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #e0e0e0;
}

.table-status-card.status-available {
  border-left-color: #28a745;
}

.table-status-card.status-reserved {
  border-left-color: #ffc107;
}

.table-status-card.status-occupied {
  border-left-color: #dc3545;
}

.table-status-card.status-maintenance {
  border-left-color: #6c757d;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.table-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.table-type {
  font-size: 12px;
  color: #666;
}

.table-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-item .label {
  font-weight: 500;
  color: #666;
}

.info-item .value {
  font-weight: 600;
}

@media (max-width: 768px) {
  .status-grid {
    grid-template-columns: 1fr;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }
}
</style>
